import { useEffect, useId, useRef, useState } from 'react';
import type { WeatherLocationOption } from '@cornerstone/shared';
import { API_BASE } from '../client/api';
import { contentGroupHeaders } from '../client/groupScope';
import { Button } from './ui';

export type WeatherLocation = Pick<WeatherLocationOption, 'placeName' | 'latitude' | 'longitude'> & { detail?: string };
export function WeatherLocationPicker({ value, onChange }: { value: WeatherLocation | null; onChange(value: WeatherLocation | null): void }) {
  const listId = useId(), inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WeatherLocationOption[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (value || query.trim().length < 2) { setState('idle'); setResults([]); return; }
    let current = true; const controller = new AbortController();
    setState('loading'); setResults([]); setActive(-1);
    const timer = setTimeout(() => {
      const deadline = setTimeout(() => controller.abort(), 15_000);
      void fetch(`${API_BASE}/api/notices/weather-locations?q=${encodeURIComponent(query.trim())}`, {
        credentials: 'include', headers: contentGroupHeaders(), signal: controller.signal,
      }).then(async response => {
        if (!response.ok) throw new Error('search failed');
        const data = await response.json() as { results: WeatherLocationOption[] };
        if (!Array.isArray(data.results)) throw new Error('invalid results');
        if (current) { setResults(data.results); setState('ready'); setActive(data.results.length ? 0 : -1); }
      }).catch(() => { if (current) setState('error'); }).finally(() => clearTimeout(deadline));
    }, 350);
    return () => { current = false; clearTimeout(timer); controller.abort(); };
  }, [query, value, retry]);
  const choose = (location: WeatherLocationOption) => { onChange(location); setOpen(false); setQuery(''); };
  const visible = open && !value && results.length > 0;
  return <div className="space-y-2 text-[13px]" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }}>
    <label htmlFor={inputId} className="block">지역 검색</label>
    {value ? <div className="flex items-center justify-between gap-3 rounded-lg border border-line bg-panel-2 p-3">
      <div className="min-w-0"><p className="font-medium text-text">{value.placeName}</p>{value.detail && <p className="mt-1 text-[12px] text-text-dim">{value.detail}</p>}</div>
      <Button type="button" onClick={() => { onChange(null); setQuery(''); setOpen(true); setTimeout(() => input.current?.focus(), 0); }}>지역 변경</Button>
    </div> : <>
      <input id={inputId} ref={input} role="combobox" aria-autocomplete="list" aria-expanded={visible}
        aria-controls={listId} aria-activedescendant={visible && active >= 0 ? `${listId}-${active}` : undefined}
        value={query} maxLength={100} placeholder="춘천, 서울, 부산 등" autoComplete="off"
        className="w-full min-w-0 rounded border border-line bg-panel-2 px-3 py-2 text-[13px] text-text"
        onFocus={() => setOpen(true)} onChange={event => { setQuery(event.target.value); setResults([]); setActive(-1); setOpen(true); }}
        onKeyDown={event => {
          if (event.nativeEvent.isComposing || event.keyCode === 229) return;
          if (event.key === 'Enter') { event.preventDefault(); if (visible && active >= 0) choose(results[active]!); }
          else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); setOpen(true); if (results.length) setActive(index => (index + (event.key === 'ArrowDown' ? 1 : -1) + results.length) % results.length);
          } else if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); }
        }} />
      {visible && <div id={listId} role="listbox" aria-label="지역 검색 결과" className="max-h-48 overflow-y-auto rounded-lg border border-line bg-panel shadow-sm">
        {results.map((location, index) => <button id={`${listId}-${index}`} key={location.id} type="button" role="option" aria-selected={active === index}
          aria-label={`${location.placeName} ${location.detail}`}
          className={`block w-full px-3 py-2 text-left hover:bg-panel-2 ${active === index ? 'bg-panel-2' : ''}`}
          onMouseDown={event => event.preventDefault()} onClick={() => choose(location)}>
          <span className="block font-medium">{location.placeName}</span><span className="block text-[12px] text-text-dim">{location.detail}</span>
        </button>)}
      </div>}
      <p role="status" className="text-[12px] text-text-dim">{state === 'loading' ? '지역을 찾는 중…' : state === 'error'
        ? '지역을 검색하지 못했습니다. 연결을 확인하고 다시 시도해 주세요.' : state === 'ready' && !results.length
          ? '검색 결과가 없습니다. 시·군 이름으로 다시 검색해 주세요.' : '지역명을 입력하고 검색 결과에서 선택해 주세요.'}</p>
      {state === 'error' && <Button type="button" onClick={() => { setRetry(count => count + 1); setOpen(true); }}>다시 시도</Button>}
    </>}
    <p className="text-[11px] text-text-dim">국내 지역 검색: Open-Meteo · GeoNames</p>
  </div>;
}
